// src/StickMenu.jsx
import { useState, useRef, useEffect } from 'react'

const STICKMENU_COOKIE = 'stickmenu_open'

function readMenuCookie() {
  const m = document.cookie.match(/(?:^|; )stickmenu_open=([^;]*)/)
  return m ? m[1] : null
}

function writeMenuCookie(value) {
  const d = new Date()
  d.setFullYear(d.getFullYear() + 1)
  document.cookie = `${STICKMENU_COOKIE}=${value}; expires=${d.toUTCString()}; path=/; SameSite=Lax`
}

// b0〜b4 など（b5/b7以外）で、JSONに無い場合に補完するためのデフォルトURL
// ※JSONがある場合は常にJSONを優先します
const DEFAULT_URLS = {
  faq: 'https://faq.fa.omron.co.jp/tech/s/',
  contact: '/contact/',
  download: '/download/',
  stock: '/product/stocksearch/?btn=dock',
  testing: '/support/selection/testing/',
  // chat は補完しない（b5/b7 の場合は欠損なら disabled にするため）
}

// download のサブメニュー項目のメタ情報（ラベル、アイコン、デフォルトURL）
const DOWNLOAD_TAB_META = {
  catalog: {
    label: 'カタログ',
    icon: '/assets/img/stick-menu/sub-icon_catalog.svg',
    disabledIcon: '/assets/img/stick-menu/sub-icon_catalog_disabled.svg',
    defaultUrl: '/download/catalogs/',
  },
  manual: {
    label: 'マニュアル',
    icon: '/assets/img/stick-menu/sub-icon_manual.svg',
    disabledIcon: '/assets/img/stick-menu/sub-icon_manual_disabled.svg',
    defaultUrl: '/download/manuals/',
  },
  cad2d: {
    label: '2D CAD',
    icon: '/assets/img/stick-menu/sub-icon_2dcad.svg',
    disabledIcon: '/assets/img/stick-menu/sub-icon_2dcad_disabled.svg',
    defaultUrl: '/download/cad2ds/',
  },
  cad3d: {
    label: '3D CAD',
    icon: '/assets/img/stick-menu/sub-icon_3dcad.svg',
    disabledIcon: '/assets/img/stick-menu/sub-icon_3dcad_disabled.svg',
    defaultUrl: '/download/cad3ds/',
  },
  // fixedメニューでは表示するが、b5/b7(dynamic)では作らない
  cade: {
    label: '電気制御CAD',
    icon: '/assets/img/stick-menu/sub-icon_ecad.svg',
    disabledIcon: '/assets/img/stick-menu/sub-icon_ecad_disabled.svg',
    defaultUrl: '/download/cades/',
  },
  software: {
    label: 'ソフトウェア',
    icon: '/assets/img/stick-menu/sub-icon_software.svg',
    disabledIcon: '/assets/img/stick-menu/sub-icon_software_disabled.svg',
    defaultUrl: '/download/softwares/',
  },
}

function isB5orB7(config) {
  const pt = config?.p_t
  return pt === 'b5' || pt === 'b7'
}

// p_t が b5/b7 以外向け：固定サブメニュー（全件有効）
function buildDownloadItemsFixed() {
  const meta = DOWNLOAD_TAB_META
  return Object.keys(meta).map((key) => {
    const m = meta[key]
    return {
      key,
      url: m.defaultUrl,
      label: m.label,
      icon: m.icon,
      disabled: false,
    }
  })
}

// p_t が b5/b7 & downloadあり：available_tabs + tabs で制御
// 仕様：b5/b7 のとき cade は「無効表示」ではなく「そもそも作らない」
// 仕様：available_tabs に入っていても tabs[key] が無ければ disabled
function buildDownloadItemsDynamic(config) {
  const meta = DOWNLOAD_TAB_META
  const dl = config?.download || {}

  const availableTabs = dl.available_tabs || []
  const tabsUrlMap = dl.tabs || {}

  return Object.keys(meta)
    .filter((key) => key !== 'cade') // ★ b5/b7(dynamic) では cade を作らない
    .map((key) => {
      const m = meta[key]
      const isAvailable = availableTabs.includes(key)
      const urlFromJson = tabsUrlMap[key]

      const enabled = isAvailable && !!urlFromJson

      return {
        key,
        url: enabled ? urlFromJson : null,
        label: m.label,
        icon: enabled ? m.icon : m.disabledIcon,
        disabled: !enabled,
      }
    })
}

// 有効/無効でDOMを切り替える（無効は div.is-disabled + span）
function DownloadSubmenuItem({ item }) {
  const { url, label, icon, disabled } = item

  if (disabled) {
    return (
      <div className="p-stickmenu__nav_submenu-btn is-disabled">
        <span className="p-stickmenu__nav_submenu-btn-link">
          <img src={icon} alt="" className="p-stickmenu__nav_submenu-icon" />
          <span className="p-stickmenu__nav_submenu-text">{label}</span>
        </span>
      </div>
    )
  }

  return (
    <div className="p-stickmenu__nav_submenu-btn">
      <a href={url} className="p-stickmenu__nav_submenu-btn-link">
        <img src={icon} alt="" className="p-stickmenu__nav_submenu-icon" />
        <span className="p-stickmenu__nav_submenu-text">{label}</span>
      </a>
    </div>
  )
}

// 共通ボタン：b5/b7(dynamic) のとき、URL欠損なら disabled DOM（div.is-disabled + span）
// disabledアイコンは icon_xxx_disabled.svg という命名ルールで参照
function NavButton({
  label,
  href,
  iconBase, // 例: 'chat' -> /assets/img/stick-menu/icon_chat.svg
  disabled,
  extraClassName, // 必要なものだけ（今回はチャットでは不要）
  target,
  rel,
  gtmLabel,
  onClick,
}) {
  const iconSrc = `/assets/img/stick-menu/icon_${iconBase}${disabled ? '_disabled' : ''}.svg`

  if (disabled) {
    return (
      <div className="p-stickmenu__nav_btn is-disabled">
        <span className="p-stickmenu__nav_link">
          <img src={iconSrc} alt="" className="p-stickmenu__nav_icon" />
          <span className="p-stickmenu__nav_text">{label}</span>
        </span>
      </div>
    )
  }

  return (
    <div className="p-stickmenu__nav_btn">
      <a
        href={href}
        className={
          'p-stickmenu__nav_link' + (extraClassName ? ` ${extraClassName}` : '')
        }
        target={target}
        rel={rel}
        onClick={onClick}
        data-gtm-send-to="ga4"
        data-gtm-trigger="click"
        data-gtm-ga-event-name="clk_iweb_dock_button"
        data-gtm-ga-event-category="I-Web Dock FY25"
        data-gtm-ga-event-action="ボタンクリック"
        data-gtm-ga-event-lavel={gtmLabel}
      >
        <img src={iconSrc} alt="" className="p-stickmenu__nav_icon" />
        <span className="p-stickmenu__nav_text">{label}</span>
      </a>
    </div>
  )
}

function StickMenu({ config }) {
  console.log('StickMenu received config:', config)

  const [hasInited, setHasInited] = useState(false)

  const [isOpen, setIsOpen] = useState(() => {
    const val = readMenuCookie()
    return val === null ? true : val === '1'
  })

  const handleToggle = () => {
    const next = !isOpen
    setIsOpen(next)
    writeMenuCookie(next ? '1' : '0')
  }

  const isDynamic = isB5orB7(config)

  // JSONそのまま（欠損検知用）
  const rawUrls = config?.urls || {}

  // 実際に href で使う URL（b0〜b4などはデフォルトで補完）
  // b5/b7は欠損でも href を補完してしまうと disabled にならないので、
  // disabled判定は必ず rawUrls を参照します。
  const urls = { ...DEFAULT_URLS, ...rawUrls }

  // b5/b7(dynamic) のときだけ、JSONにURLが無ければ disabled にする（補完後の urls ではなく rawUrls で判定）
  const disabledIfMissing = (key) => isDynamic && !rawUrls[key]

  // ダウンロードのモードを決定
  // fixed: b5/b7以外 → サブメニューは固定（全件有効）。親URLは urls.download（JSON優先 + デフォルト補完）
  // dynamic-disabled: b5/b7 かつ download無し → 親のみdisabled、サブメニュー無し
  // dynamic: b5/b7 かつ download有り → JSONに従う（available_tabs/tabsでdisabled、cadeは非表示）
  const hasDownloadProp = !!config?.download
  const downloadMode = !isDynamic ? 'fixed' : hasDownloadProp ? 'dynamic' : 'dynamic-disabled'

  const showDownloadSubmenu = downloadMode !== 'dynamic-disabled'

  const downloadItems =
    downloadMode === 'fixed'
      ? buildDownloadItemsFixed()
      : downloadMode === 'dynamic'
        ? buildDownloadItemsDynamic(config)
        : []

  // 親ダウンロードURL
  // - fixed: urls.download（JSON優先 + デフォルト補完）
  // - dynamic: config.download.download_url を優先（無ければ urls.download）
  const downloadHref =
    downloadMode === 'fixed'
      ? (urls.download || '#')
      : (config?.download?.download_url || urls.download || '#')

  // hover 開閉（サブメニューがある時だけ使う）
  const [isDownloadActive, setIsDownloadActive] = useState(false)
  const closeTimerRef = useRef(null)

  // ★ チャットを別ウィンドウで開く（既存jQueryに依存しない）
  const openChatWindow = (e) => {
    e.preventDefault()

    const windowHeight = window.innerHeight || document.documentElement.clientHeight || 800
    const winOption = `Scrollbars=yes,Status=no,resizable=yes,width=800,height=${windowHeight}`

    window.open('/contact/tech/chat/', 'chat', winOption)
  }

  useEffect(() => {
    setHasInited(true)
  }, [])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const onDownloadMouseEnter = () => {
    if (!showDownloadSubmenu) return
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setIsDownloadActive(true)
  }

  const onDownloadMouseLeave = () => {
    if (!showDownloadSubmenu) return
    closeTimerRef.current = setTimeout(() => {
      setIsDownloadActive(false)
      closeTimerRef.current = null
    }, 300)
  }

  return (
    <nav className={[
      'p-stickmenu__nav',
      !hasInited && 'p-stickmenu-init',
      !hasInited && 'u-stickmenu-preload',
      !isOpen && 'is-closed',
    ].filter(Boolean).join(' ')}>
      <button
        type="button"
        className="p-stickmenu__toggle-btn"
        onClick={handleToggle}
        aria-label={isOpen ? 'スティックメニューを閉じる' : 'スティックメニューを開く'}
      />
      <div className="p-stickmenu__nav_inner">
        {/* FAQ */}
        <NavButton
          label="FAQ"
          href={urls.faq || '#'}
          iconBase="faq"
          disabled={disabledIfMissing('faq')}
          target="_blank"
          rel="noreferrer"
          gtmLabel="「FAQ」ボタン"
        />

        {/* チャット：b5/b7で urls.chat が無ければ disabled。クリック時は window.open でポップアップ */}
        <NavButton
          label="チャット"
          href="/contact/tech/chat/"
          iconBase="chat"
          disabled={disabledIfMissing('chat')}
          extraClassName="js-login-check"
          onClick={openChatWindow}
          gtmLabel="「チャット」ボタン"
        />

        {/* お問い合わせ */}
        <NavButton
          label="お問い合わせ"
          href={urls.contact || '#'}
          iconBase="contact"
          disabled={disabledIfMissing('contact')}
          gtmLabel="「お問い合わせ」ボタン"
        />

        {/* ダウンロード（親ボタン + サブメニュー） */}
        {downloadMode === 'dynamic-disabled' ? (
          <div className="p-stickmenu__nav_btn is-disabled js-submenu_hover">
            <span className="p-stickmenu__nav_link">
              <img
                src="/assets/img/stick-menu/icon_download_disabled.svg"
                alt=""
                className="p-stickmenu__nav_icon"
              />
              <span className="p-stickmenu__nav_text">ダウンロード</span>
            </span>
          </div>
        ) : (
          <div
            className="p-stickmenu__nav_btn js-submenu_hover"
            onMouseEnter={onDownloadMouseEnter}
            onMouseLeave={onDownloadMouseLeave}
          >
            <a
              href={downloadHref}
              className="p-stickmenu__nav_link"
              data-gtm-send-to="ga4"
              data-gtm-trigger="click"
              data-gtm-ga-event-name="clk_iweb_dock_button"
              data-gtm-ga-event-category="I-Web Dock FY25"
              data-gtm-ga-event-action="ボタンクリック"
              data-gtm-ga-event-lavel="「ダウンロード」ボタン"
            >
              <img
                src="/assets/img/stick-menu/icon_download.svg"
                alt=""
                className="p-stickmenu__nav_icon"
              />
              <span className="p-stickmenu__nav_text">ダウンロード</span>
            </a>

            {showDownloadSubmenu && (
              <div
                className={
                  'p-stickmenu__nav_submenu' + (isDownloadActive ? ' is-active' : '')
                }
                id="submenu-download"
              >
                {downloadItems.map((item) => (
                  <DownloadSubmenuItem key={item.key} item={item} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 在庫・価格 */}
        <NavButton
          label="在庫・価格"
          href={urls.stock || '#'}
          iconBase="stock"
          disabled={disabledIfMissing('stock')}
          gtmLabel="「在庫・価格」ボタン"
        />

        {/* 無料テスト機 */}
        <NavButton
          label="無料テスト機"
          href={urls.testing || '#'}
          iconBase="testing"
          disabled={disabledIfMissing('testing')}
          gtmLabel="「無料テスト機」ボタン"
        />
      </div>
    </nav>
  )
}

export default StickMenu
